import styled from "styled-components";
import { FC, MouseEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { ActionsWrap, Td } from "@/components/Table";
import Text from "@/components/Text";
import { ReactComponent as TrashIcon } from "@/assets/icons/trashbin.svg";
import ActionsDropdown, {
  ActionsDropdownButton,
} from "@/components/ActionsDropdown";
import { VideoTipResource } from "@/types/resources";
import useAlerts from "@/hooks/useAlerts";
import { api } from "@/utils/api";
import { durationFormatter } from "@/utils/helpers";

const Link = styled(Text)`
  max-width: 80px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const LinkWrapper = styled.div`
  display: flex;
  align-items: center;
  svg {
    flex-shrink: 0;
    margin-right: 4px;
  }
`;

interface Props {
  tip: VideoTipResource;
  releaseId: number;
}

const DistributionScheduleTipsTableRow: FC<Props> = ({ tip, releaseId }) => {
  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const onDelete = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirmed = await alerts.confirm(
        "Are you sure you would like to detach this tip?",
        tip.name
      );

      if (!confirmed) return;

      await api.delete(`/v1/releases/${releaseId}/tips/${tip.id}`);

      await queryClient.invalidateQueries(["releases", { id: releaseId }]);

      queryClient.invalidateQueries(["releases", { videoSummary: true }]);

      alerts.success("Video tip successfully detached");
    } catch (_) {
      alerts.error();
    }
  };

  return (
    <tr>
      <Td>{tip.external_id}</Td>
      <Td>{tip.name}</Td>
      <Td>
        {tip.thumbnail ? (
          <LinkWrapper>
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect width="14" height="14" fill="#F8F8F8" />
              <path
                d="M5.69434 8.30519L8.30519 5.69434"
                stroke="#909090"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M7.6523 8.95851L6.56444 10.0464C6.2182 10.3925 5.74866 10.5869 5.25908 10.5869C4.76951 10.5868 4.29999 10.3923 3.95381 10.0461C3.60763 9.69996 3.41313 9.23045 3.41309 8.74087C3.41304 8.2513 3.60747 7.78175 3.95359 7.43551L5.04144 6.34766"
                stroke="#909090"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M8.95851 7.65236L10.0464 6.5645C10.3925 6.21826 10.5869 5.74872 10.5869 5.25914C10.5868 4.76957 10.3923 4.30006 10.0461 3.95387C9.69996 3.60769 9.23045 3.41319 8.74088 3.41315C8.2513 3.41311 7.78176 3.60753 7.43551 3.95365L6.34766 5.04151"
                stroke="#909090"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <Link
              $type="smallNormal"
              as="a"
              href={tip.thumbnail}
              target="_blank"
            >
              {tip.thumbnail}
            </Link>
          </LinkWrapper>
        ) : (
          "-"
        )}
      </Td>
      <Td>{durationFormatter(tip.duration)}</Td>
      <Td>-</Td>
      <Td>{tip.video_type ?? "-"}</Td>
      <Td>
        {tip.embed_link ? (
          <LinkWrapper>
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect width="14" height="14" fill="#F8F8F8" />
              <path
                d="M5.69434 8.30519L8.30519 5.69434"
                stroke="#909090"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M7.6523 8.95851L6.56444 10.0464C6.2182 10.3925 5.74866 10.5869 5.25908 10.5869C4.76951 10.5868 4.29999 10.3923 3.95381 10.0461C3.60763 9.69996 3.41313 9.23045 3.41309 8.74087C3.41304 8.2513 3.60747 7.78175 3.95359 7.43551L5.04144 6.34766"
                stroke="#909090"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M8.95851 7.65236L10.0464 6.5645C10.3925 6.21826 10.5869 5.74872 10.5869 5.25914C10.5868 4.76957 10.3923 4.30006 10.0461 3.95387C9.69996 3.60769 9.23045 3.41319 8.74088 3.41315C8.2513 3.41311 7.78176 3.60753 7.43551 3.95365L6.34766 5.04151"
                stroke="#909090"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <Link
              $type="smallNormal"
              as="a"
              href={tip.embed_link}
              target="_blank"
            >
              {tip.embed_link}
            </Link>
          </LinkWrapper>
        ) : (
          "-"
        )}
      </Td>
      <Td>{tip.category}</Td>
      <Td>{tip.subcategory ?? "-"}</Td>
      <Td>{tip.mashup ? "Yes" : "No"}</Td>
      <Td $actionsCol>
        <ActionsWrap>
          <ActionsDropdown zIndex={11}>
            <ActionsDropdownButton as="button" onClick={onDelete}>
              <span>
                <TrashIcon />
              </span>
              Delete
            </ActionsDropdownButton>
          </ActionsDropdown>
        </ActionsWrap>
      </Td>
    </tr>
  );
};

export default DistributionScheduleTipsTableRow;
